import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CardActions,
  Checkbox,
  Box,
  Divider
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { isDemoAccount, formatColombianDate } from '../lib/demoAccount.mjs';

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'getStatusColor',
})(({ theme, status, getStatusColor }) => ({
  borderLeft: '4px solid ' + getStatusColor(status, theme),
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
}));

const InvoiceCardItem = React.memo(({
  invoice,
  theme,
  authState,
  isSelected,
  isEditingNumber,
  editingInvoiceNumberValue,
  isSavingInvoiceNumber,
  formatCurrency,
  getStatusColor,
  handleInvoiceNumberEditStart,
  handleInvoiceNumberChange,
  handleInvoiceNumberSave,
  handleInvoiceNumberCancel,
  handleInvoiceTypeChange,
  getCustomDocTypesForItemType,
  handleInvoiceCustomDocTypeChange,
  toggleInvoiceSelected,
  handleDetailModalOpen,
  handleShowAccounting,
  handleRejectInvoice,
  handleApproveInvoice,
  isInvoiceBalanced,
  handleRetrySync
}) => {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <StyledCard elevation={3} status={invoice.estado} getStatusColor={getStatusColor}>
        <CardContent sx={{ pb: 1, flexGrow: 1 }}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
              {isEditingNumber ? (
                <>
                  <TextField
                    value={editingInvoiceNumberValue}
                    onChange={handleInvoiceNumberChange}
                    size="small"
                    autoFocus
                    disabled={isSavingInvoiceNumber}
                    InputProps={{
                      style: { fontSize: '1rem', fontWeight: 600, padding: 0 }
                    }}
                    sx={{ width: '140px' }}
                  />
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => handleInvoiceNumberSave(invoice)}
                    disabled={isSavingInvoiceNumber}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={handleInvoiceNumberCancel}
                    disabled={isSavingInvoiceNumber}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, lineHeight: 1.2 }}>
                    # {invoice.invoice_number || 'N/A'}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => handleInvoiceNumberEditStart(invoice)}
                    sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {invoice.billing_name || invoice.third_party_name || 'Sin Tercero'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                {isDemoAccount(authState?.user) ? formatColombianDate(invoice.date) : invoice.date ? format(new Date(invoice.date), 'dd/MM/yyyy') : 'N/A'}
              </Typography>
              {invoice.firebird_invoice_number && (
                <Typography variant="caption" color="primary" display="block" sx={{ fontWeight: 'bold', mt: 0.5, lineHeight: 1.2 }}>
                  Doc. Firebird: {invoice.firebird_invoice_number}
                </Typography>
              )}

              <Box mt={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={invoice.invoice_type || 'servicio'}
                    disabled={invoice.estado === 'PENDIENTE'}
                    onChange={(e) => handleInvoiceTypeChange(invoice, e.target.value)}
                  >
                    <MenuItem value="libre">Libre</MenuItem>
                    <MenuItem value="servicio">Servicio</MenuItem>
                    <MenuItem value="inventario">Inventario</MenuItem>
                    <MenuItem value="cuenta_cobro">Cuenta Cobro</MenuItem>
                  </Select>
                </FormControl>
                
                {authState?.user?.enable_custom_doc_types && invoice.invoice_type !== 'libre' && getCustomDocTypesForItemType(invoice.invoice_type).length > 0 && (
                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Documento</InputLabel>
                    <Select
                      label="Documento"
                      value={invoice.custom_doc_type || ''}
                      disabled={invoice.estado === 'PENDIENTE'}
                      onChange={(e) => handleInvoiceCustomDocTypeChange(invoice, e.target.value)}
                    >
                      <MenuItem value=""><em>Ninguno</em></MenuItem>
                      {getCustomDocTypesForItemType(invoice.invoice_type).map(dt => (
                        <MenuItem key={dt.doc_type_code} value={dt.doc_type_code}>
                          {dt.doc_type_code} - {dt.doc_type_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              </Grid>
            <Grid item xs={4} sx={{ textAlign: 'left', minWidth: '150px', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography variant="h6" component="div" sx={{ lineHeight: 1.3 }}>
                    {formatCurrency(invoice.total)}
                  </Typography>
                  {invoice.estado && (
                    <Chip 
                      label={invoice.estado} 
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor: getStatusColor(invoice.estado, theme),
                        color: theme.palette.getContrastText(getStatusColor(invoice.estado, theme)),
                        fontWeight: 'medium'
                      }}
                    />
                  )}
                </Box>
                <Checkbox
                  checked={isSelected}
                  onChange={() => toggleInvoiceSelected(invoice.id)}
                  size="small"
                  inputProps={{ 'aria-label': 'Seleccionar factura' }}
                />
              </Box>
            </Grid>
          </Grid>

          {(invoice.estado === 'SINCRONIZADO' || invoice.estado === 'ERROR') && invoice.service_response && (
            <Box mt={1.5} p={1.5} sx={{ backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: 1, border: '1px solid ' + (invoice.estado === 'ERROR' ? alpha(theme.palette.error.light, 0.3) : alpha(theme.palette.success.light, 0.3)) }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 'medium', color: invoice.estado === 'ERROR' ? theme.palette.error.dark : theme.palette.text.primary, mb: 0.5 }}>
                Respuesta del Servicio:
              </Typography>
              <Typography variant="caption" component="div" sx={{ 
                color: invoice.estado === 'ERROR' ? theme.palette.error.main : theme.palette.text.secondary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '60px',
                overflowY: 'auto',
                fontSize: '0.7rem',
                lineHeight: 1.3
              }}>
                {invoice.service_response}
              </Typography>
            </Box>
          )}
        </CardContent>

        <Divider />

        <CardActions sx={{ px: 2, pt: 0, pb: 1.5, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDetailModalOpen(invoice)}
                disabled={!invoice.estado}
                sx={{ minWidth: 110 }}
              >
                Ver Detalles
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleShowAccounting(invoice)}
                disabled={(invoice.invoice_type === 'inventario' && !isDemoAccount(authState?.user)) || !invoice.estado || invoice.estado === 'PENDIENTE'}
                sx={{ minWidth: 110 }}
              >
                Ver Causación
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {(!invoice.estado || invoice.estado === 'CAUSADA') && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleRejectInvoice(invoice)}
                    sx={{ minWidth: 110 }}
                  >
                    Desaprobar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleApproveInvoice(invoice)}
                    disabled={!isInvoiceBalanced(invoice)}
                    sx={{ minWidth: 110 }}
                  >
                    Aprobar
                  </Button>
                </>
              )}
              {invoice.estado === 'DESAPROBADO' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleApproveInvoice(invoice)}
                  disabled={!isInvoiceBalanced(invoice)}
                  sx={{ minWidth: 110 }}
                >
                  Aprobar
                </Button>
              )}
              {invoice.estado === 'ERROR' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleApproveInvoice(invoice)}
                  disabled={!isInvoiceBalanced(invoice)}
                  sx={{ minWidth: 110 }}
                >
                  Aprobar
                </Button>
              )}
            </Box>
          </Box>
        </CardActions>
      </StyledCard>
    </Grid>
  );
}, (prevProps, nextProps) => {
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isEditingNumber !== nextProps.isEditingNumber) return false;
  if (prevProps.editingInvoiceNumberValue !== nextProps.editingInvoiceNumberValue) return false;
  if (prevProps.isSavingInvoiceNumber !== nextProps.isSavingInvoiceNumber) return false;
  
  const prevInv = prevProps.invoice;
  const nextInv = nextProps.invoice;
  if (prevInv.id !== nextInv.id) return false;
  if (prevInv.estado !== nextInv.estado) return false;
  if (prevInv.invoice_type !== nextInv.invoice_type) return false;
  if (prevInv.custom_doc_type !== nextInv.custom_doc_type) return false;
  if (prevInv.invoice_number !== nextInv.invoice_number) return false;
  if (prevInv.total !== nextInv.total) return false;
  if (prevInv.service_response !== nextInv.service_response) return false;
  if (prevProps.authState?.tenantDocTypes !== nextProps.authState?.tenantDocTypes) return false;

  return true;
});

export default InvoiceCardItem;
